import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../../pages/AdminBrain.css';
import '../../pages/AdminFilms.css';

// Film read-model renderers — storyboard (visual | narration per shot, with
// the shipped film to seek), transcripts as a script (versions side by
// side, read-aloud), shot lists and the shipped spec. Lifted from the
// founder-only Films page into the Content planning module; they read the
// `spec` map the importer (scripts/import-content-plans.mjs) writes on a
// repo-backed plan: { transcripts[], shotLists[], storyboards[], shipped }.

const STATUS_LABEL = { shipped: 'Shipped', approved: 'Approved / in production', proposed: 'Proposed', archived: 'Archived', 'in-production': 'In production' };

const fmtAgo = (iso) => {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
const slugify = (s) => String(s).toLowerCase().replace(/[`*]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const textOf = (children) => React.Children.toArray(children).map((c) => (typeof c === 'string' ? c : c?.props?.children ? textOf(c.props.children) : '')).join('');
const fmtAt = (at) => {
  const n = parseFloat(at);
  if (!Number.isFinite(n)) return at || '';
  const m = Math.floor(n / 60);
  const s = n - m * 60;
  return m ? `${m}:${s.toFixed(1).padStart(4, '0')}` : `${s.toFixed(1)}s`;
};


// Plan markdown: sibling links (`bridge-film.md`, `README.md#rules`) stay
// in-app; headings get ids so the section rail can jump to them.
function Markdown({ text, plans, base }) {
  const navigate = useNavigate();
  const components = useMemo(() => {
    const heading = (Tag) => ({ children }) => <Tag id={slugify(textOf(children))}>{children}</Tag>;
    return {
      h1: heading('h1'), h2: heading('h2'), h3: heading('h3'),
      a: ({ href, children }) => {
        const m = /^([\w-]+)\.md(#.*)?$/.exec(href || '');
        if (m) {
          const to = `${base || ''}/${m[1]}${m[2] || ''}`;
          const exists = !!plans?.[m[1]];
          return <a href={to} className={`ab-link ${exists ? '' : 'is-missing'}`} onClick={(e) => { e.preventDefault(); navigate(to); }}>{children}</a>;
        }
        const external = /^https?:/i.test(href || '');
        return <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>{children}</a>;
      },
    };
  }, [plans, navigate, base]);
  return <div className="ab-md af-md"><ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{text || ''}</ReactMarkdown></div>;
}

function StatusChip({ k, children }) {
  return <span className={`af-status is-${k}`}>{children || STATUS_LABEL[k] || k}</span>;
}

// ── Transcript renderers ──────────────────────────────────────────────────────
function Beat({ b, read }) {
  if (read) {
    return (
      <li className="af-read-line">
        <span className="af-read-at">{fmtAt(b.at)}</span>
        <p>{b.text}{b.say ? <span className="af-say"> (say: {b.say})</span> : null}</p>
      </li>
    );
  }
  return (
    <li className={`af-beat ${b.over ? 'is-over' : ''}`}>
      <div className="af-beat-meta">
        <span className="af-beat-at">{fmtAt(b.at)}</span>
        {b.shot ? <span className="af-beat-shot">{b.shot}</span> : null}
      </div>
      <div className="af-beat-body">
        <p className="af-beat-text">{b.text}</p>
        {(b.say || b.note) && (
          <p className="af-beat-notes">
            {b.say ? <span className="af-say">say: {b.say}</span> : null}
            {b.note ? <span>{b.note}</span> : null}
          </p>
        )}
      </div>
      <div className="af-beat-budget" title={b.budgetNote || ''}>
        {b.words != null ? <span className="af-words">{b.words}w</span> : null}
        {b.budget != null ? <span className="af-budget">/ {b.budget}</span> : null}
        {b.recorded ? <span className="af-rec">rec</span> : null}
        {b.over ? <span className="af-over">over</span> : null}
      </div>
    </li>
  );
}

function Script({ t, read }) {
  return (
    <div className="af-script">
      <div className="af-script-head">
        <h3>{t.label || t.name}</h3>
        <span className="cs-help">
          {t.beats.length} lines · {t.totalWords ?? t.beats.reduce((a, b) => a + (b.words || 0), 0)} words
          {t.overBudget ? <> · <span className="af-over">{t.overBudget} over budget</span></> : null}
          {t.voice ? <> · voice {t.voice}</> : null}
        </span>
      </div>
      <ol className={read ? 'af-read' : 'af-beats'}>
        {t.beats.map((b, i) => <Beat key={i} b={b} read={read} />)}
      </ol>
    </div>
  );
}

// Every transcript the plan carries: its own tables, then the shipped
// narration JSON (marked shipped).
const tidy = (label) => String(label || '').replace(/^\d+\.\s*/, '');
function useVersions(plan) {
  return useMemo(() => {
    const out = (plan.transcripts || []).map((t, i) => ({ ...t, key: `t${i}`, label: tidy(t.label) }));
    for (const s of plan.spec?.scripts || []) if (!s.error) out.push({ ...s, key: `s-${s.name}`, label: `Shipped · ${s.name}.json`, overBudget: 0, shipped: true });
    return out;
  }, [plan]);
}

// ── Storyboard: visual | narration, one row per shot ─────────────────────────
// Beats join a shot by its id (the Shot column) first, else by time.
function assignBeats(shots, beats) {
  const rows = shots.map((s) => ({ shot: s, beats: [] }));
  const unplaced = [];
  const byId = new Map(shots.map((s, i) => [String(s.id).toLowerCase(), i]));
  for (const b of beats || []) {
    const ids = String(b.shot || '').split(/[/,\s]+/).map((x) => x.toLowerCase()).filter(Boolean);
    let i = ids.map((id) => byId.get(id)).find((x) => x != null);
    if (i == null) {
      const at = parseFloat(b.at);
      if (Number.isFinite(at)) i = shots.findIndex((s) => at >= s.start - 0.35 && at < s.end - 0.35);
      if (i === -1 && Number.isFinite(at) && shots.length && at >= shots[shots.length - 1].end - 0.35) i = shots.length - 1;
    }
    if (i == null || i < 0) unplaced.push(b);
    else rows[i].beats.push(b);
  }
  return { rows, unplaced };
}

function Visual({ v, large }) {
  const cls = `af-vis is-${v.kind} ${large ? 'is-large' : ''}`;
  if (v.url) {
    return (
      <figure className={cls}>
        <img src={v.url} alt={v.label} loading="lazy" />
        {v.badge ? <span className="af-vis-badge">{v.badge}</span> : null}
        <figcaption>{v.kind === 'comp' ? `overlay ${v.label}` : v.label}</figcaption>
      </figure>
    );
  }
  return (
    <figure className={`${cls} is-placeholder`}>
      <div className="af-vis-ph">
        <span className="af-vis-kind">{v.kind === 'new' ? 'new' : v.kind === 'comp' ? 'new overlay' : 'not in library'}</span>
        <span>{v.label}</span>
      </div>
    </figure>
  );
}

function StoryRow({ shot, beats, active, onSeek, lineAsText }) {
  const visuals = shot.visuals || [];
  let hero = shot.frame ? { kind: 'frame', url: shot.frame, label: `film @ ${fmtAt(shot.start + shot.dur * 0.4)}` } : visuals[0];
  let rest = shot.frame ? visuals : visuals.slice(1);
  // A new shot with a named fallback: show the fallback picture, badged.
  if (hero && hero.kind === 'new' && !hero.url) {
    const fb = rest.find((v) => v.url);
    if (fb) { hero = { ...fb, badge: hero.label, label: `fallback ${fb.label}` }; rest = rest.filter((v) => v !== fb); }
  }
  return (
    <li className={`af-row ${active ? 'is-active' : ''}`} onClick={onSeek ? () => onSeek(shot.start) : undefined} role={onSeek ? 'button' : undefined}>
      <div className="af-row-visual">
        {hero ? <Visual v={hero} large /> : <Visual v={{ kind: 'new', label: shot.source || 'no source named' }} large />}
        {rest.length ? <div className="af-strip">{rest.map((v, i) => <Visual key={i} v={v} />)}</div> : null}
        <div className="af-row-meta">
          <span className="af-beat-shot">{shot.id}</span>
          <span className="af-mono">{fmtAt(shot.start)} · {shot.dur}s</span>
          {shot.layer ? <span className="af-layer">layer {shot.layer}</span> : null}
          {shot.motion ? <span>{shot.motion}</span> : null}
        </div>
        {shot.onScreen ? <p className="af-onscreen"><Inline text={shot.onScreen} /></p> : null}
        {shot.overlay ? <p className="af-overlay">overlay: <Inline text={shot.overlay} /></p> : null}
      </div>
      <div className="af-row-text">
        {beats.length ? beats.map((b, i) => (
          <div key={i} className={`af-row-beat ${b.over ? 'is-over' : ''}`}>
            <div className="af-row-beat-meta">
              <span className="af-beat-at">{fmtAt(b.at)}</span>
              {b.words != null ? <span className="af-words">{b.words}w{b.budget != null ? ` / ${b.budget}` : ''}</span> : null}
              {b.recorded ? <span className="af-rec">rec</span> : null}
              {b.over ? <span className="af-over">over</span> : null}
            </div>
            <p className="af-row-line">{b.text}</p>
            {(b.say || b.note) && <p className="af-beat-notes">{b.say ? <span className="af-say">say: {b.say}</span> : null}{b.note ? <span>{b.note}</span> : null}</p>}
          </div>
        )) : lineAsText && shot.line ? (
          <div className="af-row-beat"><p className="af-row-line"><Inline text={shot.line} /></p></div>
        ) : <p className="af-silent">— no line —</p>}
      </div>
    </li>
  );
}

function Storyboard({ plan }) {
  const versions = useVersions(plan);
  const boards = useMemo(() => {
    const out = [];
    if (plan.spec?.storyboard) out.push({ ...plan.spec.storyboard, key: 'spec' });
    for (const [i, b] of (plan.storyboards || []).entries()) out.push({ ...b, key: `b${i}`, label: tidy(b.label) });
    return out;
  }, [plan]);
  const [bi, setBi] = useState(0);
  const [vi, setVi] = useState(() => {
    const shipped = versions.findIndex((v) => v.shipped);
    return boards[0]?.shipped && shipped >= 0 ? shipped : 0;
  });
  const [alt, setAlt] = useState(false);
  const [t, setT] = useState(-1);
  const videoRef = React.useRef(null);
  const board = boards[Math.min(bi, boards.length - 1)];
  const version = versions[vi] || null;
  const { rows, unplaced } = useMemo(() => assignBeats(board?.shots || [], version?.beats || []), [board, version]);
  if (!board) return <p className="cs-help">No shot list in this plan yet — the storyboard needs section 5 (Shot list) filled in.</p>;
  const video = board.shipped && plan.spec?.video ? (alt && plan.spec.videoAlt ? plan.spec.videoAlt : plan.spec.video) : null;
  const seek = video ? (start) => { const el = videoRef.current; if (el) { el.currentTime = Math.max(0, start + 0.05); el.play().catch(() => {}); } } : null;
  const activeIdx = t >= 0 ? board.shots.findIndex((s) => t >= s.start && t < s.end) : -1;
  const total = board.shots.length ? board.shots[board.shots.length - 1].end : 0;
  return (
    <div className="af-story">
      <div className="af-story-controls">
        {boards.length > 1 ? (
          <div className="af-versions">
            <span className="cs-help">Cut:</span>
            {boards.map((b, i) => <button key={b.key} type="button" className={`ab-chip ${i === bi ? 'is-active' : ''} ${b.shipped ? 'is-session' : ''}`} onClick={() => { setBi(i); setT(-1); }}>{b.label}</button>)}
          </div>
        ) : null}
        {versions.length ? (
          <div className="af-versions">
            <span className="cs-help">Words:</span>
            {versions.map((v, i) => <button key={v.key} type="button" className={`ab-chip ${i === vi ? 'is-active' : ''} ${v.shipped ? 'is-session' : ''}`} onClick={() => setVi(i)}>{v.label}{v.overBudget ? <span className="af-chip-over">{v.overBudget}</span> : null}</button>)}
          </div>
        ) : null}
        <span className="cs-help af-story-total">{board.shots.length} shots · {fmtAt(total)}{version ? ` · ${version.beats.length} lines` : ''}</span>
      </div>
      <div className={`af-story-body ${video ? 'has-video' : ''}`}>
        {video ? (
          <div className="af-player">
            <video ref={videoRef} src={video.src} poster={video.poster || plan.spec.video.poster || undefined} controls playsInline preload="metadata"
              onTimeUpdate={(e) => setT(e.currentTarget.currentTime)} onSeeked={(e) => setT(e.currentTarget.currentTime)}>
              {video.captions ? <track kind="captions" src={video.captions} srcLang="en" label="Narration" default /> : null}
            </video>
            <div className="af-player-foot">
              <span className="af-mono">{t >= 0 ? fmtAt(t) : '—'}{activeIdx >= 0 ? ` · ${board.shots[activeIdx].id}` : ''}</span>
              {plan.spec.videoAlt ? (
                <label className="cs-help af-toggle"><input type="checkbox" checked={alt} onChange={(e) => setAlt(e.target.checked)} /> {plan.spec.videoAlt.label} cut</label>
              ) : null}
              <span className="cs-help">Click a row to jump the film there.</span>
            </div>
          </div>
        ) : null}
        <ol className="af-rows">
          {rows.map(({ shot, beats }, i) => <StoryRow key={shot.id + i} shot={shot} beats={beats} active={i === activeIdx} onSeek={seek} lineAsText={!version} />)}
        </ol>
      </div>
      {unplaced.length ? (
        <div className="af-unplaced">
          <h4>Lines not placed on a shot of this cut ({unplaced.length})</h4>
          <p className="cs-help">Their Shot column names a shot this cut does not have and their time falls outside it — an older cut's lines, usually.</p>
          <ol className="af-beats">{unplaced.map((b, i) => <Beat key={i} b={b} />)}</ol>
        </div>
      ) : null}
    </div>
  );
}

// Versions as chips; one selected, an optional second for side-by-side;
// a read-aloud mode that drops everything but time and words.
function Transcripts({ plan }) {
  const versions = useVersions(plan);
  const [a, setA] = useState(0);
  const [b, setB] = useState(-1);
  const [read, setRead] = useState(false);
  if (!versions.length) return <p className="cs-help">No transcript tables in this plan yet.</p>;
  const A = versions[Math.min(a, versions.length - 1)];
  const B = b >= 0 ? versions[b] : null;
  return (
    <div className="af-transcripts">
      <div className="af-versions">
        {versions.map((v, i) => (
          <button key={v.key} type="button" className={`ab-chip ${i === a ? 'is-active' : ''} ${v.shipped ? 'is-session' : ''}`} onClick={() => { setA(i); if (b === i) setB(-1); }}>
            {v.label}{v.overBudget ? <span className="af-chip-over">{v.overBudget}</span> : null}
          </button>
        ))}
      </div>
      <div className="af-tools">
        <label className="cs-help">
          Compare with{' '}
          <select value={b} onChange={(e) => setB(parseInt(e.target.value, 10))}>
            <option value={-1}>—</option>
            {versions.map((v, i) => (i === a ? null : <option key={v.key} value={i}>{v.label}</option>))}
          </select>
        </label>
        <label className="cs-help af-toggle">
          <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} /> Read-aloud mode
        </label>
      </div>
      <div className={`af-compare ${B ? 'is-two' : ''}`}>
        <Script t={A} read={read} />
        {B ? <Script t={B} read={read} /> : null}
      </div>
    </div>
  );
}

function ShotLists({ plan }) {
  const lists = plan.shotLists || [];
  const shipped = plan.spec?.shots;
  if (!lists.length && !shipped) return <p className="cs-help">No shot list in this plan yet.</p>;
  return (
    <div className="af-shotlists">
      {lists.map((l, i) => (
        <div key={i} className="af-script">
          <div className="af-script-head">
            <h3>{l.label || `Shot list ${i + 1}`}</h3>
            <span className="cs-help">{l.shots.length} shots · {l.total}s</span>
          </div>
          <table className="af-table">
            <thead>
              <tr><th>#</th><th>Dur</th>{l.shots.some((s) => s.layer) && <th>Layer</th>}<th>Source</th><th>On screen</th>{l.shots.some((s) => s.overlay) && <th>Overlay</th>}{l.shots.some((s) => s.line) && <th>Line</th>}</tr>
            </thead>
            <tbody>
              {l.shots.map((s, j) => (
                <tr key={j}>
                  <td className="af-mono">{s.id}</td><td className="af-mono">{s.dur != null ? `${s.dur}s` : ''}</td>
                  {l.shots.some((x) => x.layer) && <td>{s.layer}</td>}
                  <td><Inline text={s.source} /></td><td><Inline text={s.onScreen} /></td>
                  {l.shots.some((x) => x.overlay) && <td><Inline text={s.overlay} /></td>}
                  {l.shots.some((x) => x.line) && <td className="af-line"><Inline text={s.line} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {shipped ? (
        <div className="af-script">
          <div className="af-script-head">
            <h3>Shipped · shots.json</h3>
            <span className="cs-help">{shipped.length} shots · {Math.round(shipped.reduce((a, s) => a + (s.dur || 0), 0) * 10) / 10}s{plan.spec.fps ? ` · ${plan.spec.fps} fps` : ''}{plan.spec.loop ? ' · loop' : ''}</span>
          </div>
          <table className="af-table">
            <thead><tr><th>#</th><th>Dur</th><th>Source</th><th>Stills / clip / comp</th><th>Title</th></tr></thead>
            <tbody>
              {shipped.map((s, j) => (
                <tr key={j}>
                  <td className="af-mono">{s.id}</td><td className="af-mono">{s.dur}s</td><td>{s.source}</td>
                  <td className="af-mono">{[...(s.stills || []), s.clip, s.mograph?.comp || s.mograph].filter(Boolean).join(', ')}</td>
                  <td>{typeof s.title === 'string' ? s.title : s.title ? JSON.stringify(s.title) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

// Inline markdown (backticks, emphasis) inside table cells.
function Inline({ text }) {
  if (!text) return null;
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <>{children}</> }}>{text}</ReactMarkdown>;
}

function Spec({ plan, plans, base }) {
  const spec = plan.spec;
  if (!spec) return <p className="cs-help">No shipped spec — this plan has not been produced. Spec would live at {plan.specDir ? <code>{plan.specDir}/</code> : 'a new media/ directory'}.</p>;
  return (
    <div className="af-spec">
      <p className="cs-help">Read from <code>{spec.dir}/</code> at sync time. The plan's Transcripts tab lists the narration scripts as versions too.</p>
      {spec.scripts.map((s) => (s.error
        ? <p key={s.file} className="af-over">{s.file}: {s.error}</p>
        : <Script key={s.file} t={{ ...s, label: `${s.name}.json${s.voice ? ` · ${s.voice}` : ''}` }} />))}
      {spec.storyboard ? (
        <details className="af-storyboard" open>
          <summary><h3>STORYBOARD.md</h3></summary>
          <Markdown text={spec.storyboard} plans={plans} base={base} />
        </details>
      ) : null}
    </div>
  );
}

// A plan doc from the module → the shape the renderers were written for.
export function filmView(plan) {
  const spec = plan?.spec || {};
  return { ...plan, transcripts: spec.transcripts || [], shotLists: spec.shotLists || [], storyboards: spec.storyboards || [], headings: spec.headings || [], spec: spec.shipped || null, specDir: plan?.source?.specDir || null, path: plan?.source?.repoPath || '' };
}

export { Markdown, StatusChip, Beat, Script, useVersions, Storyboard, Transcripts, ShotLists, Spec, Inline, fmtAt, fmtAgo, slugify, textOf };
